<template>
  <UDrawer
    v-model:open="isOpen"
    :dismissible="true"
    :handle="false"
    :ui="{
      content: 'w-full h-full',
      overlay: 'bg-black/50',
    }"
  >
    <template #content>
      <div class="h-full flex flex-col">
        <!-- Header -->
        <div
          class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700"
        >
          <h3 class="text-lg font-semibold">
            Panier ({{ cartStore.totalItems }} articles)
          </h3>
          <UButton
            color="neutral"
            variant="ghost"
            icon="i-lucide-x"
            @click="cartStore.closeCart"
          />
        </div>

        <!-- Cart Content -->
        <!-- DEBUG DUMP -->
        <pre
          class="text-xs text-red-500 overflow-auto max-h-40 p-2 bg-gray-100 hidden"
          >{{ JSON.stringify(cartStore.items, null, 2) }}</pre
        >
        <div class="flex-1 overflow-y-auto p-6">
          <div v-if="cartStore.items.length === 0" class="text-center py-8">
            <UIcon
              name="i-lucide-shopping-cart"
              class="w-16 h-16 mx-auto text-gray-400 mb-4"
            />
            <p class="text-gray-500">Votre panier est vide</p>
          </div>

          <div v-else class="space-y-4">
            <div
              v-for="item in cartStore.items"
              :key="item.id"
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
            >
              <!-- Item Header -->
              <div class="flex items-start gap-4">
                <img
                  :src="getImageUrl((item.meal || item.menu)?.image)"
                  :alt="(item.meal || item.menu)?.name"
                  class="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                />
                <div class="flex-1 min-w-0">
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ (item.meal || item.menu)?.name }}
                  </h4>
                  <p class="text-sm text-gray-500">
                    {{
                      item.type === "menu"
                        ? "Menu"
                        : item.type === "customized"
                        ? "Personnalisé"
                        : "Standard"
                    }}
                  </p>
                  <p class="text-sm font-medium text-main-color">
                    {{ item.totalPrice.toFixed(2) }}€
                  </p>
                </div>
                <div class="flex flex-col gap-1">
                  <UButton
                    v-if="
                      item.type === 'menu' ||
                      (item.meal &&
                        !['Boissons', 'Desserts'].includes(
                          item.meal.group || item.meal.category?.name,
                        ))
                    "
                    color="gray"
                    variant="ghost"
                    icon="i-lucide-pencil"
                    size="sm"
                    @click="editItem(item)"
                  />
                  <UButton
                    color="red"
                    variant="ghost"
                    icon="i-lucide-trash-2"
                    size="sm"
                    @click="cartStore.removeItem(item.id)"
                  />
                </div>
              </div>

              <!-- Content Display -->
              <div class="mt-3">
                <!-- Meal Ingredients (Regular/Customized) -->
                <template v-if="item.meal">
                  <div class="mt-2 space-y-1">
                    <div
                      v-for="disp in getUnifiedDisplayIngredients(
                        item.meal,
                        item.extras,
                      )"
                      :key="disp.name"
                      class="flex justify-between text-sm text-gray-600 dark:text-gray-400"
                    >
                      <span
                        v-if="disp.currentQty === 0 && disp.baseQty > 0"
                        class="text-red-500 font-medium"
                      >
                        Sans {{ disp.name }}
                      </span>
                      <span v-else>
                        {{ disp.name }} {{ formatIngredientQuantity(disp) }}
                      </span>
                      <span
                        v-if="!(disp.currentQty === 0 && disp.baseQty > 0)"
                        :class="{
                          'text-gray-400': disp.totalExtraCost === 0,
                          'text-main-color font-medium':
                            disp.totalExtraCost > 0,
                        }"
                      >
                        {{
                          disp.totalExtraCost > 0
                            ? `+${disp.totalExtraCost.toFixed(2)}€`
                            : "Inclus"
                        }}
                      </span>
                    </div>
                  </div>
                </template>

                <!-- Menu Content -->
                <template v-if="item.type === 'menu' && item.menu">
                  <div class="mb-3">
                    <p
                      class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      Contenu du menu :
                    </p>
                    <ul
                      class="text-sm text-gray-600 dark:text-gray-400 space-y-2"
                    >
                      <li
                        v-for="content in item.menu.menu_content"
                        :key="content.id"
                      >
                        <div class="flex justify-between">
                          <span class="font-medium">{{
                            content.repas.name
                          }}</span>
                        </div>
                        <!-- Show ingredients for this meal -->
                        <div
                          class="pl-2 mt-1 border-l-2 border-gray-200 space-y-1"
                        >
                          <div
                            v-for="disp in getUnifiedDisplayIngredients(
                              content.repas,
                              item.menuCustomizations?.find(
                                (c) => c.menuContentId === content.id,
                              )?.extras,
                            )"
                            :key="disp.name"
                            class="flex justify-between text-xs text-gray-600 dark:text-gray-400"
                          >
                            <span
                              v-if="disp.currentQty === 0 && disp.baseQty > 0"
                              class="text-red-500 font-medium"
                            >
                              Sans {{ disp.name }}
                            </span>
                            <span v-else>
                              {{ disp.name }}
                              {{ formatIngredientQuantity(disp) }}
                            </span>
                            <span
                              v-if="
                                !(disp.currentQty === 0 && disp.baseQty > 0)
                              "
                              :class="{
                                'text-gray-400': disp.totalExtraCost === 0,
                                'text-main-color font-medium':
                                  disp.totalExtraCost > 0,
                              }"
                            >
                              {{
                                disp.totalExtraCost > 0
                                  ? `+${disp.totalExtraCost.toFixed(2)}€`
                                  : "Inclus"
                              }}
                            </span>
                          </div>
                        </div>
                      </li>
                    </ul>
                    <!-- Selected Sauces -->
                    <div
                      v-if="
                        item.selectedSauces && item.selectedSauces.length > 0
                      "
                      class="mt-2"
                    >
                      <p class="text-xs font-medium text-gray-500 mb-1">
                        Sauces :
                      </p>
                      <div class="flex flex-wrap gap-1">
                        <UBadge
                          v-for="s in item.selectedSauces"
                          :key="s.sauce.id"
                          color="gray"
                          variant="soft"
                          size="md"
                        >
                          {{ s.quantity > 1 ? `${s.quantity}x ` : ""
                          }}{{ s.sauce.name }}
                        </UBadge>
                      </div>
                    </div>
                    <!-- Selected Desserts -->
                    <div
                      v-if="
                        item.selectedDesserts &&
                        item.selectedDesserts.length > 0
                      "
                      class="mt-2"
                    >
                      <p class="text-xs font-medium text-gray-500 mb-1">
                        Desserts :
                      </p>
                      <div class="flex flex-wrap gap-1">
                        <UBadge
                          v-for="d in item.selectedDesserts"
                          :key="d.dessert.id"
                          color="gray"
                          variant="soft"
                          size="sm"
                        >
                          {{ d.quantity > 1 ? `${d.quantity}x ` : ""
                          }}{{ d.dessert.name }}
                        </UBadge>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <!-- Quantity Controls -->
              <div class="flex items-center justify-between mt-3">
                <div class="flex items-center gap-2">
                  <UButton
                    color="neutral"
                    variant="outline"
                    icon="i-lucide-minus"
                    size="sm"
                    @click="
                      cartStore.updateQuantity(item.id, item.quantity - 1)
                    "
                  />
                  <span class="w-8 text-center">{{ item.quantity }}</span>
                  <UButton
                    color="neutral"
                    variant="outline"
                    icon="i-lucide-plus"
                    size="sm"
                    @click="
                      cartStore.updateQuantity(item.id, item.quantity + 1)
                    "
                  />
                </div>
                <div class="text-sm text-gray-500">
                  {{
                    (item.meal ? item.meal.price : item.menu?.price)?.toFixed(
                      2,
                    )
                  }}€ base
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          v-if="cartStore.items.length > 0"
          class="border-t border-gray-200 dark:border-gray-700 p-6"
        >
          <div class="flex justify-between items-center mb-4">
            <span class="text-lg font-semibold">Total :</span>
            <span class="text-xl font-bold text-main-color">
              {{ cartStore.totalPrice.toFixed(2) }}€
            </span>
          </div>
          <div class="flex gap-3">
            <UButton
              color="neutral"
              variant="outline"
              class="flex-1"
              @click="cartStore.clearCart"
            >
              Vider le panier
            </UButton>
            <UButton color="main" class="flex-1" @click="handleCheckout">
              Commander
            </UButton>
          </div>
        </div>
      </div>
    </template>
  </UDrawer>

  <!-- Edit Meal Drawer -->
  <MealCustomizeDrawer
    v-if="currentMealToEdit"
    v-model="isMealEditOpen"
    :meal="currentMealToEdit"
    :existing-extras="existingExtrasOfEdit"
    :auto-add-to-cart="false"
    @meal-customized="handleMealEdited"
  />

  <!-- Edit Menu Drawer -->
  <MenuCustomizeDrawer
    v-if="currentMenuToEdit"
    v-model="isMenuEditOpen"
    :menu="currentMenuToEdit"
    :existing-customizations="existingMenuCustomizationsOfEdit"
    :existing-sauces="existingSaucesOfEdit"
    :existing-desserts="existingDessertsOfEdit"
    :auto-add-to-cart="false"
    @menu-customized="handleMenuEdited"
  />
</template>

<script setup lang="ts">
import type { CartItem } from "~/stores/cart";
import type { Ingredient } from "~/models/repas";

const config = useRuntimeConfig();
const getImageUrl = (path: string | undefined) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${config.public.apiUrl}${path}`;
};

const cartStore = useCartStore();

// Handle v-model binding for UDrawer
const isOpen = computed({
  get: () => cartStore.isOpen,
  set: (value) => {
    if (!value) cartStore.closeCart();
    else cartStore.openCart();
  },
});

// --- Robust Parsing Helper (Duplicated from cart.ts logic for self-containment) ---
const parseOriginalIngredients = (
  meal: Repas,
): Record<string, { quantity: number; name: string }> => {
  const originalIngredients: Record<
    string,
    { quantity: number; name: string }
  > = {};

  // Method 1: Use structured content (Preferred)
  if (meal.content && Array.isArray(meal.content) && meal.content.length > 0) {
    meal.content.forEach(
      (item: { ingredient?: { name: string }; quantity: number }) => {
        if (item.ingredient && item.ingredient.name) {
          originalIngredients[item.ingredient.name.toLowerCase()] = {
            quantity: item.quantity,
            name: item.ingredient.name, // Keep casing
          };
        }
      },
    );
    return originalIngredients;
  }

  // Method 2: Fallback to string parsing
  if (meal.ingredients) {
    const ingredientStrings = meal.ingredients
      .split(",")
      .map((s: string) => s.trim());

    ingredientStrings.forEach((ingredientString: string) => {
      const match = ingredientString.match(/^(\d+)\s+(.+)$/);
      if (match) {
        const quantity = parseInt(match[1]);
        const ingredientName = match[2].trim();
        originalIngredients[ingredientName.toLowerCase()] = {
          quantity,
          name: ingredientName,
        };
      } else if (ingredientString.length > 0) {
        // Implicit quantity 1
        const ingredientName = ingredientString.trim();
        originalIngredients[ingredientName.toLowerCase()] = {
          quantity: 1,
          name: ingredientName,
        };
      }
    });
  }
  return originalIngredients;
};

// --- Display Logic Helper ---
interface IngredientDisplay {
  name: string;
  baseQty: number;
  addedQty: number;
  currentQty: number;
  pricePerUnit: number;
  totalExtraCost: number;
}

const getUnifiedDisplayIngredients = (
  meal: Repas,
  extras: Array<{ ingredient: Ingredient; quantity: number }> | undefined,
): IngredientDisplay[] => {
  const originals = parseOriginalIngredients(meal);
  const displayMap = new Map<string, IngredientDisplay>();

  // 1. Add all originals
  Object.values(originals).forEach((orig) => {
    displayMap.set(orig.name.toLowerCase(), {
      name: orig.name,
      baseQty: orig.quantity,
      addedQty: 0,
      currentQty: orig.quantity,
      pricePerUnit: 0,
      totalExtraCost: 0,
    });
  });

  // 2. Process extras (Updates or Adds)
  if (extras) {
    extras.forEach((extra) => {
      const key = extra.ingredient.name.toLowerCase();
      const existing = displayMap.get(key);

      if (existing) {
        // Updated quantity
        const added = Math.max(0, extra.quantity - existing.baseQty);
        existing.addedQty = added;
        existing.currentQty = extra.quantity;
        existing.pricePerUnit = extra.ingredient.price;
        existing.totalExtraCost = added * extra.ingredient.price;
      } else {
        // Completely new extra
        displayMap.set(key, {
          name: extra.ingredient.name,
          baseQty: 0,
          addedQty: extra.quantity,
          currentQty: extra.quantity,
          pricePerUnit: extra.ingredient.price,
          totalExtraCost: extra.quantity * extra.ingredient.price,
        });
      }
    });
  }

  // Filter out items that are strictly 0 quantity AND 0 base quantity (ghosts)
  // But keep items that are 0 currentQty but >0 baseQty (Removed)
  return Array.from(displayMap.values()).filter(
    (d) => d.currentQty > 0 || d.baseQty > 0,
  );
};

const formatIngredientQuantity = (item: IngredientDisplay) => {
  if (item.currentQty === 0 && item.baseQty > 0) {
    return ""; // Will be handled by template name replacement
  }

  // Format: "Steak (1 + 1)" or "Steak (+1)" or "Steak (1)" based on user req
  if (item.baseQty > 0 && item.addedQty > 0) {
    return `(${item.baseQty} + ${item.addedQty})`;
  } else if (item.baseQty > 0 && item.addedQty === 0) {
    // Check if quantity reduced?
    if (item.currentQty < item.baseQty) {
      return `(${item.currentQty})`;
    }
    return `(${item.baseQty})`;
  } else if (item.baseQty === 0 && item.addedQty > 0) {
    return `(+${item.addedQty})`;
  }
  return "";
};

const router = useRouter();

const handleCheckout = async () => {
  cartStore.closeCart();
  await router.push("/checkout");
};

// Edit Logic
const isMealEditOpen = ref(false);
const isMenuEditOpen = ref(false);
const itemToEditId = ref<string | null>(null);
const currentMealToEdit = ref<Repas | null>(null);
const existingExtrasOfEdit = ref<CartItem["extras"]>([]);
const currentMenuToEdit = ref<Menu | null>(null);
const existingMenuCustomizationsOfEdit = ref<CartItem["menuCustomizations"]>(
  [],
);
const existingSaucesOfEdit = ref<CartItem["selectedSauces"]>([]);
const existingDessertsOfEdit = ref<CartItem["selectedDesserts"]>([]);

const editItem = (item: CartItem) => {
  if (item.type === "regular" || item.type === "customized") {
    if (!item.meal) return;
    itemToEditId.value = item.id;
    currentMealToEdit.value = item.meal;
    existingExtrasOfEdit.value = item.extras || [];
    isMealEditOpen.value = true;
  } else if (item.type === "menu") {
    if (!item.menu) return;
    itemToEditId.value = item.id;
    currentMenuToEdit.value = item.menu;
    existingMenuCustomizationsOfEdit.value = item.menuCustomizations || [];
    existingSaucesOfEdit.value = item.selectedSauces || [];
    existingDessertsOfEdit.value = item.selectedDesserts || [];
    isMenuEditOpen.value = true;
  }
};

const handleMealEdited = (data: {
  meal: Repas;
  extras: NonNullable<CartItem["extras"]>;
}) => {
  if (itemToEditId.value) {
    cartStore.updateCustomizedMeal(itemToEditId.value, data.extras);
    isMealEditOpen.value = false;
    itemToEditId.value = null;
  }
};

const handleMenuEdited = (data: {
  menu: Menu;
  customizations: NonNullable<CartItem["menuCustomizations"]>;
  sauces: NonNullable<CartItem["selectedSauces"]>;
  desserts: NonNullable<CartItem["selectedDesserts"]>;
}) => {
  if (itemToEditId.value) {
    cartStore.updateMenu(
      itemToEditId.value,
      data.customizations,
      data.sauces,
      data.desserts,
    );
    isMenuEditOpen.value = false;
    itemToEditId.value = null;
  }
};
</script>
